/**
 * LLM 管理 API 服务
 *
 * 封装 LLM 管理相关的 REST API 调用，用于管理后台和模型选择。
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * 管理 API 请求封装：附带用户体系 Bearer token（is_admin 用户认证兜底），
 * 后端优先校验 admin_token / access_token Cookie
 */
async function adminFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers || {});
  const userToken =
    sessionStorage.getItem('access_token') ||
    localStorage.getItem('token') ||
    localStorage.getItem('access_token');
  if (userToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${userToken}`);
  }
  return fetch(input, { ...init, headers, credentials: init.credentials ?? 'include' });
}

export interface AvailableModelsResponse {
  models: string[];
  last_validated: string | null;
}

export interface AvailableModelWithLatency {
  provider: string;
  model_name: string;
  model_use: string;
  response_time_ms: number;
  pool_index: number;
}

export interface AvailableModelsWithLatencyResponse {
  models: AvailableModelWithLatency[];
  total_count: number;
  avg_latency_ms: number;
  min_latency_ms: number;
  max_latency_ms: number;
}

export interface OverviewStats {
  total_requests_today: number;
  total_tokens_today: number;
  active_users_today: number;
  error_rate_today: number;
  total_providers: number;
  total_pools: number;
  total_models: number;
}

export interface UsageStats {
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  total_tokens: number;
  input_tokens: number;
  output_tokens: number;
  avg_latency_ms: number | null;
  error_rate: number;
}

export interface PoolStats {
  provider_name: string;
  pool_index: number;
  usage_stats: UsageStats;
  models: string[];
}

export interface ModelStats {
  model_use: string;
  model_name: string;
  provider_name: string | null;
  usage_stats: UsageStats;
  pool_count: number;
}

export interface TrendPoint {
  timestamp: string;
  value: number;
}

export interface TrendData {
  requests: TrendPoint[];
  tokens: TrendPoint[];
  errors: TrendPoint[];
  latency: TrendPoint[];
}

export interface UserDistributionItem {
  user_id: string;
  username?: string;
  nickname?: string;
  request_count: number;
  total_tokens: number;
  input_tokens: number;
  output_tokens: number;
  avg_latency_ms: number | null;
  error_count: number;
}

export interface PoolConfig {
  provider_name: string;
  pool_index: number;
  base_url: string;
  api_key_preview: string;
  models: string[];
  quota_limits: {
    token: number | false;
    cps: number | false;
    rpm: number | false;
    five_hour_num: number | false;
    day_num: number | false;
    week_num: number | false;
    month_num: number | false;
  };
  quota?: QuotaLimitNested;
}

export interface LLMSettings {
  provider: Record<string, {
    base_url: string;
    pool: Array<{
      api_key: string;
      base_url?: string;
      quota?: QuotaLimitNested;
      token?: number | false;
      cps?: number | false;
      rpm?: number | false;
      five_hour_num?: number | false;
      day_num?: number | false;
      week_num?: number | false;
      month_num?: number | false;
      model: Array<{
        model_name: string;
        model_use: string;
        model_type?: string;
        base_url?: string;
        quota?: QuotaLimitNested;
        health?: ModelHealth;
        token?: number | false;
        day_num?: number | false;
      }>;
    }>;
  }>;
}

export interface HealthCheckResult {
  timestamp: string;
  total_keys: number;
  valid_keys: number;
  invalid_keys: number;
  total_models: number;
  valid_models: number;
  invalid_models: number;
  success_rate: number;
  alert_triggered: boolean;
}

export interface ProviderCreate {
  name: string;
  base_url: string;
}

export interface ProviderUpdate {
  base_url?: string;
}

export interface PoolCreate {
  api_key: string;
  quota_limits?: {
    second_num?: number | false;
    minute_num?: number | false;
    hour_num?: number | false;
    tpm?: number | false;
    five_hour_num?: number | false;
    day_num?: number | false;
    week_num?: number | false;
    month_num?: number | false;
  };
}

export interface PoolUpdate {
  api_key?: string;
  quota_limits?: {
    second_num?: number | false;
    minute_num?: number | false;
    hour_num?: number | false;
    tpm?: number | false;
    five_hour_num?: number | false;
    day_num?: number | false;
    week_num?: number | false;
    month_num?: number | false;
  };
}

export interface ModelCreate {
  model_name: string;
  model_use: string;
  model_type?: string;
  quota_limits?: {
    day_num?: number | false;
  };
}

export interface ModelUpdate {
  model_name?: string;
  quota_limits?: {
    day_num?: number | false;
  };
}

export interface PriorityItem {
  provider_name: string;
  pool_index: number;
  priority: number;
}

export interface QuotaTypeDetail {
  limit: number;
  used: number;
  remaining: number;
  reset_at?: string;
}

export interface QuotaUsageResponse {
  providers: Record<string, Record<number, {
    second_num?: QuotaTypeDetail;
    minute_num?: QuotaTypeDetail;
    hour_num?: QuotaTypeDetail;
    tpm?: QuotaTypeDetail;
    five_hour_num?: QuotaTypeDetail;
    day_num?: QuotaTypeDetail;
    week_num?: QuotaTypeDetail;
    month_num?: QuotaTypeDetail;
  }>>;
  timestamp: string;
}

export interface TokenQuota {
  per_minute: number | false;
  per_hour: number | false;
  per_day: number | false;
  per_month: number | false;
  total: number | false;
}

export interface RequestQuota {
  per_minute: number | false;
  per_hour: number | false;
  per_day: number | false;
  per_month: number | false;
  total: number | false;
}

export interface ModelHealth {
  available: boolean;
  latency_ms: number;
  last_check: string | null;
  error_count: number;
}

export interface QuotaLimitNested {
  concurrency: number | false;
  token: TokenQuota;
  request: RequestQuota;
}

export interface QuotaUsageItemNew {
  provider_name: string;
  pool_index: number;
  model_use: string;
  quota_type: string;
  used_count: number;
  limit_value: number | null;
  remaining: number | null;
  percentage: number | null;
  reset_at: string | null;
}

export interface QuotaUsageResponseNew {
  usage: QuotaUsageItemNew[];
  total: number;
  timestamp: string;
}

export interface QuotaResetRequest {
  provider_name: string;
  pool_index: number;
  model_use: string;
  quota_type?: string;
}

export interface QuotaResetResponse {
  success: boolean;
  reset_count: number;
  message: string;
}

export interface QuotaFlushResponse {
  success: boolean;
  flushed_count: number;
  message: string;
}

export interface ModelHealthItem {
  provider_name: string;
  pool_index: number;
  model_use: string;
  model_name: string;
  available: boolean;
  latency_ms: number;
  error_count: number;
  last_check: string | null;
}

export interface ModelHealthResponse {
  models: ModelHealthItem[];
  total: number;
  available_count: number;
  unavailable_count: number;
}

export interface ModelValidateRequest {
  provider_name?: string;
  pool_index?: number;
}

export interface ModelValidateResultItem {
  provider_name: string;
  pool_index: number;
  model_name: string;
  valid: boolean;
  response_time_ms: number;
  error_message: string | null;
}

export interface ModelValidateResponse {
  model_use: string;
  results: ModelValidateResultItem[];
  total_validated: number;
  valid_count: number;
  timestamp: string;
}

export interface ErrorStatsResponse {
  categories: Record<string, {
    count: number;
    percentage: number;
    codes: Record<string, number>;
  }>;
  total_errors: number;
  date_range: { start: string; end: string };
}

export interface SourceStatsResponse {
  sources: Record<string, {
    request_count: number;
    total_tokens: number;
    avg_latency_ms: number | null;
  }>;
  date_range: { start: string; end: string };
}

export interface LatencyStatsResponse {
  by_model: Record<string, {
    avg_ms: number | null;
    min_ms: number | null;
    max_ms: number | null;
    sample_count: number;
  }>;
  by_provider: Record<string, {
    avg_ms: number | null;
    min_ms: number | null;
    max_ms: number | null;
    sample_count: number;
  }>;
  date_range: { start: string; end: string };
}

export interface LoadDataResponse {
  groups: Array<{
    model_use: string;
    model_type: string;
    data: Array<{
      timestamp: string;
      request_count: number;
      token_count: number;
    }>;
  }>;
  date_range: { start: string; end: string };
  granularity: string;
}

export interface ValidationResult {
  valid: boolean;
  error_message?: string;
  response_time_ms?: number;
  timestamp: string;
}

export interface ApiKeyStatusItem {
  key_index: number;
  key_preview: string;
  status: 'enabled' | 'disabled' | 'exhausted';
  used_quota: number;
  error_count: number;
  disabled_reason: string | null;
  disabled_time: string | null;
}

export interface ApiKeyListResponse {
  provider_name: string;
  pool_index: number;
  key_selection_mode: string;
  keys: ApiKeyStatusItem[];
  total: number;
  enabled_count: number;
}

export interface ApiKeyOperationResponse {
  success: boolean;
  message: string;
}

export interface ApiKeyUsageItem {
  key_index: number;
  key_preview: string;
  used_quota: number;
  error_count: number;
}

export interface ApiKeyUsageResponse {
  provider_name: string;
  pool_index: number;
  keys: ApiKeyUsageItem[];
  total: number;
}

export interface AlertConfigItem {
  scope: string;
  provider_name: string | null;
  pool_index: number | null;
  warning_threshold: number;
  critical_threshold: number;
  notification_type: string;
  webhook_url: string | null;
  cooldown_minutes: number;
  enabled: boolean;
  quota_types: string[] | null;
}

export interface AlertConfigUpdateRequest {
  scope: string;
  warning_threshold?: number;
  critical_threshold?: number;
  notification_type?: string;
  webhook_url?: string | null;
  webhook_headers?: Record<string, string> | null;
  cooldown_minutes?: number;
  enabled?: boolean;
  quota_types?: string[] | null;
  provider_name?: string | null;
  pool_index?: number | null;
}

export interface AlertItem {
  id: string;
  type: string;
  level: string;
  title: string;
  message: string;
  timestamp: string;
  provider: string | null;
  pool_index: number | null;
  model_use: string | null;
  resolved: boolean;
}

export interface AlertListResponse {
  alerts: AlertItem[];
  total: number;
}

function getAuthHeaders(token?: string): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message = errorData.detail || errorData.message || '请求失败';

    if (response.status === 401) {
      throw new Error(`未授权: ${message}`);
    }
    if (response.status === 403) {
      throw new Error(`没有权限: ${message}`);
    }
    if (response.status === 404) {
      throw new Error(`资源不存在: ${message}`);
    }

    throw new Error(message);
  }

  return response.json();
}

/**
 * LLM Admin API 客户端
 */
export const llmAdminApi = {
  /**
   * 获取可用模型列表
   */
  async getAvailableModels(token?: string, availableOnly: boolean = true): Promise<AvailableModelsResponse> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/llm/models?available_only=${availableOnly}`,
      {
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse<AvailableModelsResponse>(response);
  },

  /**
   * 获取可用模型列表（带延迟）
   */
  async getAvailableModelsWithLatency(token?: string): Promise<AvailableModelsWithLatencyResponse> {
    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/llm/models/available`,
      {
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse<AvailableModelsWithLatencyResponse>(response);
  },

  /**
   * 获取概览统计
   */
  async getOverviewStats(token?: string): Promise<OverviewStats> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/stats/overview`, {
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<OverviewStats>(response);
  },

  /**
   * 获取池统计
   */
  async getPoolStats(
    token?: string,
    start?: string,
    end?: string,
    provider?: string
  ): Promise<PoolStats[]> {
    const params = new URLSearchParams();
    if (start) params.append('start', start);
    if (end) params.append('end', end);
    if (provider) params.append('provider', provider);

    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/llm/stats/pools?${params.toString()}`,
      {
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse<PoolStats[]>(response);
  },

  /**
   * 获取模型统计
   */
  async getModelStats(
    token?: string,
    start?: string,
    end?: string,
    model?: string
  ): Promise<ModelStats[]> {
    const params = new URLSearchParams();
    if (start) params.append('start', start);
    if (end) params.append('end', end);
    if (model) params.append('model', model);

    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/llm/stats/models?${params.toString()}`,
      {
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse<ModelStats[]>(response);
  },

  /**
   * 获取趋势数据
   */
  async getTrends(
    token?: string,
    start?: string,
    end?: string,
    interval: 'hour' | 'day' = 'hour'
  ): Promise<TrendData> {
    const params = new URLSearchParams();
    if (start) params.append('start', start);
    if (end) params.append('end', end);
    params.append('interval', interval);

    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/llm/stats/trends?${params.toString()}`,
      {
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse<TrendData>(response);
  },

  /**
   * 获取用户分布统计
   */
  async getUserStats(
    token?: string,
    start?: string,
    end?: string,
    limit: number = 10
  ): Promise<UserDistributionItem[]> {
    const params = new URLSearchParams();
    if (start) params.append('start', start);
    if (end) params.append('end', end);
    params.append('limit', limit.toString());

    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/llm/stats/users?${params.toString()}`,
      {
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse<UserDistributionItem[]>(response);
  },

  /**
   * 触发健康检查
   */
  async validateSettings(token?: string): Promise<HealthCheckResult> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/settings/validate`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<HealthCheckResult>(response);
  },

  /**
   * 导出统计为 JSON
   */
  async exportJson(
    token?: string,
    start?: string,
    end?: string
  ): Promise<Record<string, unknown>> {
    const params = new URLSearchParams();
    if (start) params.append('start', start);
    if (end) params.append('end', end);

    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/llm/export/json?${params.toString()}`,
      {
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse<Record<string, unknown>>(response);
  },

  /**
   * 导出统计为 CSV
   */
  async exportCsv(
    token?: string,
    dimension: 'pool' | 'model' | 'user' = 'pool',
    start?: string,
    end?: string
  ): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('dimension', dimension);
    if (start) params.append('start', start);
    if (end) params.append('end', end);

    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/llm/export/csv?${params.toString()}`,
      {
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );

    if (!response.ok) {
      throw new Error('导出失败');
    }

    return response.blob();
  },

  /**
   * 获取实时监控数据
   */
  async getLiveMonitoring(token?: string): Promise<Record<string, unknown>> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/monitoring/live`, {
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<Record<string, unknown>>(response);
  },

  /**
   * 获取设置配置
   */
  async getSettings(token?: string): Promise<{ settings: LLMSettings; last_validated: string | null }> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/settings`, {
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<{ settings: LLMSettings; last_validated: string | null }>(response);
  },

  /**
   * 获取池列表
   */
  async getPools(token?: string, provider?: string): Promise<PoolConfig[]> {
    const params = new URLSearchParams();
    if (provider) params.append('provider', provider);

    const response = await adminFetch(
      `${API_BASE_URL}/api/admin/llm/settings/pools?${params.toString()}`,
      {
        headers: getAuthHeaders(token),
        credentials: 'include',
      }
    );
    return handleResponse<PoolConfig[]>(response);
  },

  /**
   * 更新设置
   */
  async updateSettings(token: string | undefined, settings: LLMSettings): Promise<{ success: boolean; message: string }> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/settings`, {
      method: 'PUT',
      headers: getAuthHeaders(token),
      credentials: 'include',
      body: JSON.stringify({ settings }),
    });
    return handleResponse<{ success: boolean; message: string }>(response);
  },

  // ========== Provider CRUD ==========

  async createProvider(token: string | undefined, data: ProviderCreate): Promise<{ success: boolean; message: string }> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/settings/provider`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse<{ success: boolean; message: string }>(response);
  },

  async updateProvider(token: string | undefined, name: string, data: ProviderUpdate): Promise<{ success: boolean; message: string }> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/settings/provider/${encodeURIComponent(name)}`, {
      method: 'PUT',
      headers: getAuthHeaders(token),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse<{ success: boolean; message: string }>(response);
  },

  async deleteProvider(token: string | undefined, name: string): Promise<{ success: boolean; message: string }> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/settings/provider/${encodeURIComponent(name)}`, {
      method: 'DELETE',
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<{ success: boolean; message: string }>(response);
  },

  // ========== Pool CRUD ==========

  async createPool(token: string | undefined, providerName: string, data: PoolCreate): Promise<{ success: boolean; message: string }> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/settings/provider/${encodeURIComponent(providerName)}/pool`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse<{ success: boolean; message: string }>(response);
  },

  async updatePool(token: string | undefined, providerName: string, poolIndex: number, data: PoolUpdate): Promise<{ success: boolean; message: string }> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/settings/provider/${encodeURIComponent(providerName)}/pool/${poolIndex}`, {
      method: 'PUT',
      headers: getAuthHeaders(token),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse<{ success: boolean; message: string }>(response);
  },

  async deletePool(token: string | undefined, providerName: string, poolIndex: number): Promise<{ success: boolean; message: string }> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/settings/provider/${encodeURIComponent(providerName)}/pool/${poolIndex}`, {
      method: 'DELETE',
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<{ success: boolean; message: string }>(response);
  },

  // ========== Model CRUD ==========

  async createModel(token: string | undefined, providerName: string, poolIndex: number, data: ModelCreate): Promise<{ success: boolean; message: string }> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/settings/provider/${encodeURIComponent(providerName)}/pool/${poolIndex}/model`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse<{ success: boolean; message: string }>(response);
  },

  async updateModel(token: string | undefined, providerName: string, poolIndex: number, modelUse: string, data: ModelUpdate): Promise<{ success: boolean; message: string }> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/settings/provider/${encodeURIComponent(providerName)}/pool/${poolIndex}/model/${encodeURIComponent(modelUse)}`, {
      method: 'PUT',
      headers: getAuthHeaders(token),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse<{ success: boolean; message: string }>(response);
  },

  async deleteModel(token: string | undefined, providerName: string, poolIndex: number, modelUse: string): Promise<{ success: boolean; message: string }> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/settings/provider/${encodeURIComponent(providerName)}/pool/${poolIndex}/model/${encodeURIComponent(modelUse)}`, {
      method: 'DELETE',
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<{ success: boolean; message: string }>(response);
  },

  // ========== Priority Management ==========

  async getPriorities(token?: string): Promise<PriorityItem[]> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/settings/priority`, {
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<PriorityItem[]>(response);
  },

  async updatePriorities(token: string | undefined, priorities: Array<{ provider_name: string; pool_index: number; priority: number }>): Promise<{ success: boolean; message: string }> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/settings/priority`, {
      method: 'PUT',
      headers: getAuthHeaders(token),
      credentials: 'include',
      body: JSON.stringify({ priorities }),
    });
    return handleResponse<{ success: boolean; message: string }>(response);
  },

  // ========== Statistics ==========

  async getQuotaUsage(token?: string): Promise<QuotaUsageResponse> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/stats/quotas`, {
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<QuotaUsageResponse>(response);
  },

  async getErrorStats(token?: string, start?: string, end?: string): Promise<ErrorStatsResponse> {
    const params = new URLSearchParams();
    if (start) params.append('start', start);
    if (end) params.append('end', end);
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/stats/errors?${params.toString()}`, {
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<ErrorStatsResponse>(response);
  },

  async getSourceStats(token?: string, start?: string, end?: string): Promise<SourceStatsResponse> {
    const params = new URLSearchParams();
    if (start) params.append('start', start);
    if (end) params.append('end', end);
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/stats/sources?${params.toString()}`, {
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<SourceStatsResponse>(response);
  },

  async getLatencyStats(token?: string, start?: string, end?: string): Promise<LatencyStatsResponse> {
    const params = new URLSearchParams();
    if (start) params.append('start', start);
    if (end) params.append('end', end);
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/stats/latency?${params.toString()}`, {
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<LatencyStatsResponse>(response);
  },

  async getLoadData(token?: string, start?: string, end?: string, groupBy?: 'model_type' | 'provider', timeRange?: string): Promise<LoadDataResponse> {
    const params = new URLSearchParams();
    if (start) params.append('start', start);
    if (end) params.append('end', end);
    if (groupBy) params.append('group_by', groupBy);
    if (timeRange) params.append('time_range', timeRange);
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/stats/load?${params.toString()}`, {
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<LoadDataResponse>(response);
  },

  // ========== Quota Management ==========

  async getQuotaUsageNew(token?: string, provider?: string, poolIndex?: number): Promise<QuotaUsageResponseNew> {
    const params = new URLSearchParams();
    if (provider) params.append('provider', provider);
    if (poolIndex !== undefined) params.append('pool_index', poolIndex.toString());
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/quota/usage?${params.toString()}`, {
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<QuotaUsageResponseNew>(response);
  },

  async getPoolQuotaUsage(provider: string, poolIndex: number, modelUse?: string, token?: string): Promise<QuotaUsageResponseNew> {
    const params = new URLSearchParams();
    if (modelUse) params.append('model_use', modelUse);
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/quota/usage/${encodeURIComponent(provider)}/${poolIndex}?${params.toString()}`, {
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<QuotaUsageResponseNew>(response);
  },

  async resetQuotaUsage(token: string | undefined, data: QuotaResetRequest): Promise<QuotaResetResponse> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/quota/reset`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse<QuotaResetResponse>(response);
  },

  async flushQuotaToDb(token?: string): Promise<QuotaFlushResponse> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/quota/flush`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<QuotaFlushResponse>(response);
  },

  // ========== Model Health ==========

  async getModelHealth(token?: string, provider?: string, availableOnly?: boolean): Promise<ModelHealthResponse> {
    const params = new URLSearchParams();
    if (provider) params.append('provider', provider);
    if (availableOnly) params.append('available_only', 'true');
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/models/health?${params.toString()}`, {
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<ModelHealthResponse>(response);
  },

  async validateModelByUse(token: string | undefined, modelUse: string, data?: ModelValidateRequest): Promise<ModelValidateResponse> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/models/${encodeURIComponent(modelUse)}/validate`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      credentials: 'include',
      body: data ? JSON.stringify(data) : '{}',
    });
    return handleResponse<ModelValidateResponse>(response);
  },

  // ========== Validation ==========

  async validateModel(token: string | undefined, modelUse: string): Promise<ValidationResult> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/validate/model/${encodeURIComponent(modelUse)}`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<ValidationResult>(response);
  },

  async validatePool(token: string | undefined, provider: string, poolIndex: number): Promise<ValidationResult> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/validate/pool/${encodeURIComponent(provider)}/${poolIndex}`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<ValidationResult>(response);
  },

  async quickValidate(token?: string): Promise<Record<string, ValidationResult>> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/validate/quick`, {
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<Record<string, ValidationResult>>(response);
  },

  async listPoolKeys(token: string | undefined, providerName: string, poolIndex: number): Promise<ApiKeyListResponse> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/pools/${encodeURIComponent(providerName)}/${poolIndex}/keys`, {
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<ApiKeyListResponse>(response);
  },

  async addPoolKey(token: string | undefined, providerName: string, poolIndex: number, key: string): Promise<ApiKeyOperationResponse> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/pools/${encodeURIComponent(providerName)}/${poolIndex}/keys`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      credentials: 'include',
      body: JSON.stringify({ key }),
    });
    return handleResponse<ApiKeyOperationResponse>(response);
  },

  async updatePoolKey(token: string | undefined, providerName: string, poolIndex: number, keyIndex: number, newKey: string): Promise<ApiKeyOperationResponse> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/pools/${encodeURIComponent(providerName)}/${poolIndex}/keys/${keyIndex}`, {
      method: 'PUT',
      headers: getAuthHeaders(token),
      credentials: 'include',
      body: JSON.stringify({ key: newKey }),
    });
    return handleResponse<ApiKeyOperationResponse>(response);
  },

  async deletePoolKey(token: string | undefined, providerName: string, poolIndex: number, keyIndex: number): Promise<ApiKeyOperationResponse> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/pools/${encodeURIComponent(providerName)}/${poolIndex}/keys/${keyIndex}`, {
      method: 'DELETE',
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<ApiKeyOperationResponse>(response);
  },

  async disablePoolKey(token: string | undefined, providerName: string, poolIndex: number, keyIndex: number, reason?: string): Promise<ApiKeyOperationResponse> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/pools/${encodeURIComponent(providerName)}/${poolIndex}/keys/${keyIndex}/disable`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      credentials: 'include',
      body: JSON.stringify(reason ? { reason } : '{}'),
    });
    return handleResponse<ApiKeyOperationResponse>(response);
  },

  async enablePoolKey(token: string | undefined, providerName: string, poolIndex: number, keyIndex: number): Promise<ApiKeyOperationResponse> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/pools/${encodeURIComponent(providerName)}/${poolIndex}/keys/${keyIndex}/enable`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<ApiKeyOperationResponse>(response);
  },

  async getPoolKeyUsage(token: string | undefined, providerName: string, poolIndex: number, keyIndex: number): Promise<ApiKeyUsageResponse> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/pools/${encodeURIComponent(providerName)}/${poolIndex}/keys/${keyIndex}/usage`, {
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<ApiKeyUsageResponse>(response);
  },

  async getAlertConfigs(token?: string): Promise<AlertConfigItem[]> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/alerts/config`, {
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<AlertConfigItem[]>(response);
  },

  async updateAlertConfig(token: string | undefined, data: AlertConfigUpdateRequest): Promise<{ success: boolean; message: string }> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/alerts/config`, {
      method: 'PUT',
      headers: getAuthHeaders(token),
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse<{ success: boolean; message: string }>(response);
  },

  async getAlerts(token?: string, resolved?: boolean, limit?: number): Promise<AlertListResponse> {
    const params = new URLSearchParams();
    if (resolved) params.append('resolved', 'true');
    if (limit) params.append('limit', limit.toString());
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/alerts?${params.toString()}`, {
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<AlertListResponse>(response);
  },

  async resolveAlert(token: string | undefined, alertId: string): Promise<{ success: boolean; message: string }> {
    const response = await adminFetch(`${API_BASE_URL}/api/admin/llm/alerts/${encodeURIComponent(alertId)}/resolve`, {
      method: 'POST',
      headers: getAuthHeaders(token),
      credentials: 'include',
    });
    return handleResponse<{ success: boolean; message: string }>(response);
  },
};

export default llmAdminApi;
